import React, { useContext, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ColWithGap, RowForTagsAndBadges } from '@components/styles';
import SelectedCarrierTag from '@components/framework/tags/SelectedCarrierTag.tsx';
import CarPlateTag from '@components/framework/tags/CarPlateTag.tsx';
import AssignedDriverTag from '@components/framework/tags/AssignedDriverTag.tsx';
import RouteTable from '@components/ringil3/Features/Expedition/RouteTable/RouteTable';
import {
  CarrierQuoteStateEnum,
  ExpeditionDetailV4OutDTO,
  ExpeditionStatusEnum,
  FeaturesEnum,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import SvgLkw from '@components/ringil3/icons/heading/Lkw';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import FedexTrackingPanel from '@components/RingilComponents/FedexTrackingPanel.tsx';
import ArrangedPriceTag from '@components/framework/tags/ArrangedPriceTag.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import ExtraCostsTag from '@components/framework/tags/ExtraCostsTag.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';

interface ComponentProps {
  expedition: ExpeditionDetailV4OutDTO;
  onReload: () => void;
  canEdit: boolean;
  secured?: SecuredConfig;
}

const TabExpeditionAssignedPlus: React.FC<ComponentProps> = ({ expedition, onReload, canEdit, secured }) => {
  const { tr } = useTranslationLgs();

  const { expEditCarInfo, sendExternalQuote } = useContext(ApiContext);
  const { decorate } = useSecuredDecoration(secured);

  const submitToSapFlag = useSecuredFlag('assigned-submit-to-sap', {
    kind: 'action',
    description: 'Submit expedition to SAP button',
    features: [FeaturesEnum.SKODA_PUBLISH_EXPEDITION_TO_SAP],
    state: [
      { label: 'expeditionStatus !== FINISHED', satisfied: expedition.expeditionStatus !== ExpeditionStatusEnum.FINISHED, description: 'Not offered on a finished shipment', byDesign: true },
      { label: 'expeditionStatus === ASSIGNED', satisfied: expedition.expeditionStatus === ExpeditionStatusEnum.ASSIGNED, description: 'Offered only while the shipment has the Assigned status', byDesign: true },
    ],
    data: [
      { label: 'no ladenFactorParentExpedition', satisfied: !expedition.ladenFactorParentExpedition },
      { label: 'ladenFactorExpeditions.length == 0', satisfied: expedition.ladenFactorExpeditions.length == 0 },
    ],
  });

  const editFinalPriceFlag = useSecuredFlag('assigned-edit-final-price', {
    kind: 'action',
    description: 'Edit final price (distribution + arranged) on a finished expedition',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_FULL_CHANGE_PRICE],
    state: [{ label: 'distribution exists', satisfied: expedition.isDistributionCreated }],
  });

  const [visibleSapSubmitDialog, setVisibleSapSubmitDialog] = useState(false);

  const arrangedPrice = useMemo(() => {
    return expedition.sumPrice?.agreedPriceOutDTO;
  }, [expedition]);

  const saveCarPlate = (carPlate: string) => {
    expEditCarInfo(expedition.id, { carPlate }, () => {
      onReload();
    });
  };
  const saveDriverName = (driverName: string) => {
    expEditCarInfo(expedition.id, { driverName: driverName, carPlate: expedition.carPlate }, () => {
      onReload();
    });
  };

  const sendExpeditionToSap = () => {
    sendExternalQuote(
      expedition.id,
      () => {
        onReload();
      });
  };

  return (
    <>{decorate(<>
      <ColWithGap>
        <div className="flex flex-row justify-between">
          <COHeadline variant={'h2'} title={tr(`ExpeditionDetail.carrierTitle`, `Carriers`)}
                      icon={<SvgLkw />}></COHeadline>
          <SecuredArea id="assigned-submit-to-sap" label="Submit to SAP" description="Submit the shipment with its assigned carrier to SAP" visible={submitToSapFlag}>
            <RingilButton
              mode={'table'}
              onClick={() => setVisibleSapSubmitDialog(true)}
              label={tr('TabExpeditionArrangedQuotes.submitToSap', 'Submit to SAP')}
            />
          </SecuredArea>
        </div>
        <RowForTagsAndBadges>
          <Ringil3BadgeRow>
            {arrangedPrice && (
              <ArrangedPriceTag
                priceDto={arrangedPrice}
                readOnly={!editFinalPriceFlag.granted}
                expeditionId={expedition.id}
                onReload={onReload}
                dataTestId={'carrierSelection-price'}
              />
            )}
            {expedition.sumPrice && (<ExtraCostsTag sumPrice={expedition.sumPrice} />)}
            <SelectedCarrierTag
              carrierName={expedition.assignedCarrierName}
              carrierId={null}
              companyLocationCustomerId={expedition.customerId}
              readOnly={true}
              dataTestId={'carrierSelection-carrierName'}
            />
            <CarPlateTag 
              carPlate={expedition.carPlate} 
              onChange={carPlate => saveCarPlate(carPlate)} 
              readOnly={!canEdit}
              dataTestId={'carrierSelection-carPlate'}
            />
            <AssignedDriverTag 
              assignedDriverName={expedition.assignedDriver} 
              readOnly={true} 
              onChange={driverName => saveDriverName(driverName)}
              dataTestId={'carrierSelection-driverName'}
            />
          </Ringil3BadgeRow>
        </RowForTagsAndBadges>
        <RouteTable
          routes={expedition.routes}
          onChanged={() => onReload()}
          applicationId={expedition.applicationId}
          companyId={expedition.customerId}
          canEdit={canEdit}
          locations={expedition.locations}
        />
        {/* renders itself only for FedEx expeditions that already have a shipment */}
        <FedexTrackingPanel expeditionId={expedition.id} />
      </ColWithGap>
      <ConfirmDialog
        visible={visibleSapSubmitDialog}
        dialogTitle={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionTitle', 'Do you want to submit to SAP the expedition?')}
        dialogMessage={tr('ButtonMoreNavigation.doYouWantToSubmitToSapTheExpeditionMessage', 'Do you want to submit to SAP the expedition?')}
        onCancel={() => setVisibleSapSubmitDialog(false)}
        onConfirm={() => {
          sendExpeditionToSap();
          setVisibleSapSubmitDialog(false);
        }}
        onAcceptButtonText={tr(`ButtonMoreNavigation.submit`, `Submit`)}
        onCancelButtonText={tr(`ButtonMoreNavigation.close`, `Close`)}
      />
    </>)}</>
  );
};

export default TabExpeditionAssignedPlus;
